import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// Debug logging helper - matches original debugLog pattern
function debugLog(label, details = null) {
  const prefix = `[MarketStore ${new Date().toISOString()}] ${label}`;
  if (details === null || details === undefined) {
    console.log(prefix);
    return;
  }
  console.log(prefix, details);
}

export const useMarketStore = defineStore('market', () => {
  const prices = ref({
    gold: { price: 0, change: 0, changePercent: 0 },
    silver: { price: 0, change: 0, changePercent: 0 },
    copper: { price: 0, change: 0, changePercent: 0 },
    bitcoin: { price: 0, change: 0, changePercent: 0 },
  });

  const lastUpdated = ref(null);
  const refreshInterval = ref(60000);
  const isLoading = ref(false);
  const error = ref(null);

  const commodityData = ref({
    gold: { chartData: [], timingWindows: [], signals: [] },
    silver: { chartData: [], timingWindows: [], signals: [] },
    copper: { chartData: [], timingWindows: [], signals: [] },
    bitcoin: { chartData: [], timingWindows: [], signals: [] },
  });

  async function fetchPrices() {
    debugLog('market.fetch.start', { endpoint: '/api/market/prices' });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch('/api/market/prices');
      if (!response.ok) throw new Error('Failed to fetch prices');
      const data = await response.json();
      debugLog('market.fetch.response', { status: response.status, ok: response.ok });
      prices.value = data;
      lastUpdated.value = new Date();
      debugLog('market.fetch.success', {
        gold: prices.value.gold?.price,
        silver: prices.value.silver?.price,
        copper: prices.value.copper?.price,
        bitcoin: prices.value.bitcoin?.price
      });
    } catch (e) {
      debugLog('market.fetch.error', { error: e.message });
      console.warn('Market price fetch failed; using cached or empty data.', e);
      error.value = e.message;
    } finally {
      isLoading.value = false;
      debugLog('market.fetch.complete', { isLoading: isLoading.value });
    }
  }

  async function fetchCommodityData(commodity, days = 365) {
    debugLog('commodity.fetch.start', { commodity, days, endpoint: `/api/market/${commodity}?days=${days}` });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`/api/market/${commodity}?days=${days}`);
      if (!response.ok) throw new Error(`Failed to fetch ${commodity} data`);
      const data = await response.json();
      debugLog('commodity.fetch.response', { status: response.status, commodity, points: data.chartData?.length });
      commodityData.value[commodity] = data;
      debugLog('commodity.fetch.success', {
        commodity,
        hasChartData: Array.isArray(data.chartData) && data.chartData.length > 0,
        hasTimingWindows: Array.isArray(data.timingWindows) && data.timingWindows.length > 0
      });
    } catch (e) {
      debugLog('commodity.fetch.error', { commodity, error: e.message });
      console.warn(`Commodity ${commodity} fetch failed; using cached or empty data.`, e);
      error.value = e.message;
    } finally {
      isLoading.value = false;
      debugLog('commodity.fetch.complete', { commodity, isLoading: isLoading.value });
    }
  }

  const getPrice = computed((commodity) => {
    const price = prices.value[commodity]?.price || 0;
    return price;
  });

  const getChange = computed((commodity) => prices.value[commodity]?.change || 0);
  const getChangePercent = computed((commodity) => prices.value[commodity]?.changePercent || 0);

  debugLog('market.store.init', { refreshInterval: refreshInterval.value });

  return {
    prices,
    lastUpdated,
    refreshInterval,
    isLoading,
    error,
    commodityData,
    fetchPrices,
    fetchCommodityData,
    getPrice,
    getChange,
    getChangePercent,
  };
});
